import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useAuth } from '@/services/auth';
import { DEMO_MODE } from '@/demo/config';
import {
  listMyCoachTickets, fetchReadStates, isTicketUnread, subscribeToMyConversations,
  type SupportTicket, type ReadState,
} from '@/services/support';

/**
 * The signed-in creator's coach tickets and read cursors, shared by the nav
 * badge, the Support inbox and the chat screen, so opening a thread clears the
 * badge without a second fetch path. Refreshes on realtime thread changes, on
 * tab focus, and every 90s as a fallback.
 */
interface SupportState {
  tickets: SupportTicket[];
  reads: Record<string, ReadState>;
  loading: boolean;
  error: string | null;
  unreadCount: number;
  refresh: () => Promise<void>;
  /** Clears one thread's unread state locally, before the refetch lands. */
  markReadLocal: (conversationId: string) => void;
}

const SupportContext = createContext<SupportState | null>(null);

export function SupportProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const userId = DEMO_MODE ? null : user?.id ?? null;
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [reads, setReads] = useState<Record<string, ReadState>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const inflight = useRef<Promise<void> | null>(null);

  // A refresh asked for while one is in flight runs again once it lands, so a
  // read receipt written mid-fetch is never overwritten by the older result.
  const again = useRef(false);
  const refresh = useCallback(async (): Promise<void> => {
    if (!userId) { setLoading(false); return; }
    if (inflight.current) { again.current = true; return inflight.current; }
    const run = (async () => {
      try {
        const [t, r] = await Promise.all([listMyCoachTickets(), fetchReadStates(userId)]);
        setTickets(t);
        setReads(r);
        setError(null);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not load your support requests.');
      } finally {
        setLoading(false);
        inflight.current = null;
      }
    })();
    inflight.current = run;
    await run;
    if (again.current) { again.current = false; await refresh(); }
  }, [userId]);

  useEffect(() => {
    setTickets([]); setReads({}); setLoading(true);
    if (!userId) { setLoading(false); return; }
    refresh();
    const unsubscribe = subscribeToMyConversations(userId, () => { refresh(); });
    const onFocus = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onFocus);
    const interval = window.setInterval(refresh, 90_000);
    return () => { unsubscribe(); document.removeEventListener('visibilitychange', onFocus); window.clearInterval(interval); };
  }, [userId, refresh]);

  const markReadLocal = useCallback((conversationId: string) => {
    setReads((prev) => ({ ...prev, [conversationId]: { last_read_at: new Date().toISOString() } }));
  }, []);

  const value = useMemo<SupportState>(() => ({
    tickets, reads, loading, error, refresh, markReadLocal,
    unreadCount: tickets.filter((t) => isTicketUnread(t, reads)).length,
  }), [tickets, reads, loading, error, refresh, markReadLocal]);

  return <SupportContext.Provider value={value}>{children}</SupportContext.Provider>;
}

export function useSupport(): SupportState {
  const ctx = useContext(SupportContext);
  if (!ctx) throw new Error('useSupport must be used within SupportProvider');
  return ctx;
}

/** Nav badge helper that tolerates being rendered outside the provider. */
export function useSupportUnreadCount(): number {
  return useContext(SupportContext)?.unreadCount ?? 0;
}
